// src/pages/Profile.tsx — account hub, matches iOS ProfileView
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User, CreditCard, HardDrive, Lock, LifeBuoy, Info, FileText,
  Trash2, LogOut, ChevronRight, ChevronLeft, MessageSquare,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiService } from "../services/api";
import BottomNav from "../components/BottomTabBar";
import SupportTicketModal from "../components/SupportTicketModal";

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tier, setTier] = useState<string>(user?.tier || "FREE");
  const [storage, setStorage] = useState<{ used: number; total: number } | null>(null);
  const [showSupport, setShowSupport] = useState(false);

  // Theme State
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains("dark")
  );

  const toggleTheme = () => {
    if (isDarkMode) {
      document.documentElement.classList.remove("dark");
      setIsDarkMode(false);
    } else {
      document.documentElement.classList.add("dark");
      setIsDarkMode(true);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const sub = await apiService.getSubscription();
        if (alive && (sub?.planName || sub?.plan?.name)) setTier(sub.planName || sub.plan?.name);
      } catch { /* ignore */ }
      try {
        const s = await apiService.getStorageDetails();
        if (!alive) return;
        const usedGb = (s?.usedBytes ?? s?.used_bytes ?? s?.summary?.usedBytes ?? 0) / 1_073_741_824;
        const totalGb = s?.quotaGb ?? s?.quota_gb ?? s?.totalGb ?? s?.summary?.baseStorageGb ?? 0;
        setStorage({ used: usedGb, total: totalGb });
      } catch { /* ignore */ }
    };

    refresh();
    // Re-fetch whenever the user returns to this tab/page so plan & storage stay current.
    const onFocus = () => refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initials =
    `${(user?.firstName?.[0] ?? "").toUpperCase()}${(user?.lastName?.[0] ?? "").toUpperCase()}` ||
    (user?.email?.[0] ?? "U").toUpperCase();
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Vastavik User";

  const storagePct = storage ? Math.min(100, (storage.used / (storage.total || 1)) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-[#0a0a0a] text-black dark:text-white pb-24 font-['Product_Sans'] transition-colors duration-300">
      
      {/* Navbar */}
      <nav className="
        flex items-center justify-between
        h-[72px] px-6 md:px-10
        bg-white dark:bg-black
        border-b border-gray-200 dark:border-[#2C2C2C]
        transition-colors duration-300
        sticky top-0 z-50
      ">
        <div className="flex-1 flex justify-start">
          <span 
            onClick={() => navigate("/dashboard")}
            className="font-['Caviar_Dreams'] text-[24px] md:text-[28px] text-black dark:text-white tracking-widest cursor-pointer"
          >
            VASTAVIK
          </span>
        </div>

  <div className="hidden md:flex flex-1 justify-center items-center gap-8 md:gap-12 h-full">
          {(["dashboard", "library", "upload", "profile"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => {
                if (tab === "profile") return;
                // If you are using a state-driven tab switcher in your parent container,
                // replace the navigation logic here or dispatch a custom event:
                window.dispatchEvent(
                  new CustomEvent("vastavik:switch-tab", { detail: { tab } })
                );
                navigate("/dashboard", { replace: true });
              }}
              className={`relative h-full flex flex-col items-center justify-center font-['Product_Sans'] text-[15px] transition-colors ${
                tab === "profile" 
                  ? "text-black dark:text-white font-bold"
                  : "text-gray-500 dark:text-[#A0A0A0] hover:text-black dark:hover:text-white font-normal"
              }`}
            >
              <span className="capitalize pb-1">{tab}</span>
              {tab === "profile" && (
                <span className="absolute bottom-0 left-0 w-full h-[2px] bg-black dark:bg-white rounded-t-sm" />
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 flex justify-end items-center gap-4">
          <button
            onClick={toggleTheme}
            title="Toggle Theme"
            className="text-gray-400 hover:text-black dark:hover:text-white transition-colors flex items-center justify-center"
          >
            {isDarkMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
            )}
          </button>
          <div className="hidden sm:block text-[13px] text-gray-700 dark:text-[#E0E0E0] font-['Product_Sans'] truncate max-w-[150px] lg:max-w-[200px]">
            {user?.email ?? ""}
          </div>
          <button
            onClick={handleLogout}
            title="Log out"
            className="text-[#E57373] hover:opacity-80 transition-opacity flex items-center justify-center ml-2"
          >
            <LogOut size={20} strokeWidth={2} />
          </button>
        </div>
      </nav>

      <div className="max-w-[1104px] mx-auto px-6 md:px-10 pt-8">
        {/* Header */}
  
<button
  onClick={() => navigate("/dashboard", { state: { activeTab: "profile" } })}
  className="flex items-center gap-2 text-sm text-gray-500 hover:text-black dark:hover:text-white transition mb-8"
>
  <ChevronLeft size={16} />
  <span>Back</span>
</button>

        <h1 className="font-['Caviar_Dreams'] text-[40px] md:text-[44px] leading-tight font-bold text-black dark:text-white mb-10">
          Account Settings
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Profile & Storage Cards */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Profile Card */}
            <div className="bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-[#2C2C2C] rounded-[12px] shadow-sm dark:shadow-none p-8 flex flex-col items-center text-center">
              <div className="w-[88px] h-[88px] bg-[#7C3AED] rounded-full flex items-center justify-center text-[32px] font-bold text-white mb-5 shadow-inner">
                {initials}
              </div>
              <div className="text-[18px] font-semibold text-black dark:text-white mb-1">
                {fullName}
              </div>
              <div className="text-[14px] text-gray-500 dark:text-gray-400 mb-5">
                {user?.email}
              </div>
              <span className="text-[10px] font-bold bg-black dark:bg-[#333333] text-white dark:text-gray-300 px-3 py-1.5 rounded-full uppercase tracking-wider">
                {tier} TIER
              </span>
            </div>

            {/* Storage Usage Card */}
            <div className="bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-[#2C2C2C] rounded-[12px] shadow-sm dark:shadow-none p-7 flex flex-col">
              <div className="flex justify-between items-center mb-5">
                <span className="text-[12px] text-gray-500 dark:text-gray-400 tracking-wider uppercase font-medium">
                  Storage Usage
                </span>
                <span className="text-[13px] text-gray-500 dark:text-gray-400">
                  {Math.round(storagePct)}% used
                </span>
              </div>
              
              <div className="w-full h-1.5 bg-gray-200 dark:bg-[#333333] rounded-full overflow-hidden mb-6">
                <div
                  className="h-full bg-black dark:bg-white rounded-full transition-all"
                  style={{ width: `${storagePct}%` }}
                />
              </div>

              <div className="flex justify-between mb-8">
                <div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-500 tracking-wider uppercase mb-1">
                    Used
                  </div>
                  <div className="text-[14px] text-black dark:text-white font-medium">
                    {storage ? storage.used.toFixed(1) : "0.0"} GB
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-gray-500 dark:text-gray-500 tracking-wider uppercase mb-1">
                    Total Capacity
                  </div>
                  <div className="text-[14px] text-black dark:text-white font-medium">
                    {storage ? storage.total.toFixed(1) : "0.0"} GB
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate("/storage")}
                className="w-full py-3.5 rounded-[8px] bg-transparent border border-gray-300 dark:border-[#4B4B4B] text-black dark:text-white text-[14px] font-medium hover:bg-gray-50 dark:hover:bg-[#2C2C2C] transition"
              >
                Upgrade Storage
              </button>
            </div>
          </div>

          {/* Right Column: Settings Lists */}
          <div className="lg:col-span-7 flex flex-col">
            {/* Account Settings Section */}
            <div className="mb-8">
              <h2 className="text-[12px] text-gray-500 dark:text-gray-400 tracking-wider uppercase mb-3 px-1">
                Account Settings
              </h2>
              <div className="bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-[#2C2C2C] rounded-[12px] shadow-sm dark:shadow-none flex flex-col overflow-hidden">
                <button
                  onClick={() => navigate("/profile/edit")}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-white/5 transition border-b border-gray-200 dark:border-[#2C2C2C]"
                >
                  <User size={18} className="text-gray-500 dark:text-gray-400" />
                  <span className="flex-1 text-left text-[15px] text-black dark:text-white font-light">Edit Profile</span>
                  <ChevronRight size={16} className="text-gray-400 dark:text-gray-600" />
                </button>
                <button
                  onClick={() => navigate("/profile/password")}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-white/5 transition"
                >
                  <Lock size={18} className="text-gray-500 dark:text-gray-400" />
                  <span className="flex-1 text-left text-[15px] text-black dark:text-white font-light">Change Password</span>
                  <ChevronRight size={16} className="text-gray-400 dark:text-gray-600" />
                </button>
              </div>
            </div>

            {/* Support & Legal Section */}
            <div className="mb-8">
              <h2 className="text-[12px] text-gray-500 dark:text-gray-400 tracking-wider uppercase mb-3 px-1">
                Support & Legal
              </h2>
              <div className="bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-[#2C2C2C] rounded-[12px] shadow-sm dark:shadow-none flex flex-col overflow-hidden">
                <button
                  onClick={() => setShowSupport(true)}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-white/5 transition border-b border-gray-200 dark:border-[#2C2C2C]"
                >
                  <MessageSquare size={18} className="text-gray-500 dark:text-gray-400" />
                  <span className="flex-1 text-left text-[15px] text-black dark:text-white font-light">Support Tickets</span>
                  <ChevronRight size={16} className="text-gray-400 dark:text-gray-600" />
                </button>
                <button
                  onClick={() => navigate("/help")}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-white/5 transition border-b border-gray-200 dark:border-[#2C2C2C]"
                >
                  <LifeBuoy size={18} className="text-gray-500 dark:text-gray-400" />
                  <span className="flex-1 text-left text-[15px] text-black dark:text-white font-light">Help Center</span>
                  <ChevronRight size={16} className="text-gray-400 dark:text-gray-600" />
                </button>
                <button
                  onClick={() => navigate("/about")}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-white/5 transition border-b border-gray-200 dark:border-[#2C2C2C]"
                >
                  <Info size={18} className="text-gray-500 dark:text-gray-400" />
                  <span className="flex-1 text-left text-[15px] text-black dark:text-white font-light">About Vastavik</span>
                  <ChevronRight size={16} className="text-gray-400 dark:text-gray-600" />
                </button>
                <button
                  onClick={() => navigate("/terms")}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-white/5 transition"
                >
                  <FileText size={18} className="text-gray-500 dark:text-gray-400" />
                  <span className="flex-1 text-left text-[15px] text-black dark:text-white font-light">Terms & Privacy</span>
                  <ChevronRight size={16} className="text-gray-400 dark:text-gray-600" />
                </button>
              </div>
            </div>

            {/* Danger Actions Section */}
            <div>
              <div className="bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-[#2C2C2C] rounded-[12px] shadow-sm dark:shadow-none flex flex-col overflow-hidden">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-red-50 dark:hover:bg-white/5 transition border-b border-gray-200 dark:border-[#2C2C2C] text-red-500 dark:text-[#E57373]"
                >
                  <LogOut size={18} />
                  <span className="flex-1 text-left text-[15px] font-light">Log Out</span>
                </button>
                <button
                  onClick={() => navigate("/profile/delete")}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-red-50 dark:hover:bg-white/5 transition text-red-500 dark:text-[#E57373]"
                >
                  <Trash2 size={18} />
                  <span className="flex-1 text-left text-[15px] font-light">Delete Account</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BottomNav
        active="profile"
        onNavigate={(tab) => {
          if (tab === "profile") return;
          navigate(tab === "dashboard" ? "/dashboard" : `/${tab}`);
        }}
      />

      <SupportTicketModal open={showSupport} onClose={() => setShowSupport(false)} />
    </div>
  );
}